import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { Consol } from '../../../shared/models/consol.model';
import { ConsolGridOrderBy } from '../../../shared/models/consol-grid-order-by.enum';
import { ConsolListFilterRequest } from '../../../shared/models/consol-list-filter-request.model';
import { TrackerDataService } from '../../services/tracker-data.service';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { Router, ActivatedRoute, RouterStateSnapshot } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TrackingTrackerFilterPanelComponent } from 'src/app/shared/components/tracking-tracker-filter-panel/tracking-tracker-filter-panel.component';
import { FormsModule } from '@angular/forms';
import { ConsolidationListTableComponent } from 'src/app/shared/components/consolidation-list-table/consolidation-list-table.component';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';


@Component({
  selector: 'app-tracker-list', 
  templateUrl: './tracker-list.component.html',
  imports: [CommonModule, TrackingTrackerFilterPanelComponent, FormsModule, ConsolidationListTableComponent, NgbPaginationModule, CommonModule]
})
export class TrackerListComponent implements OnInit, OnDestroy {
  public consolList: Consol[];
  public filter: ConsolListFilterRequest;
  public readonly columns = ConsolGridOrderBy;
  public currentPage = 1;
  public totalCount: number;
  public readonly pageSize = 25;
  public routerlinkPathFn: (consol: any) => string;
  private ngUnsubscribe = new Subject<void>();
  public NoResultMessage: string;

  private defaultFilter: ConsolListFilterRequest = {
    MasterBillNumber: '',
    ContainerNumber: '',
    HouseBillNumber: '',
    CustomerRefNumber: '',
    Skip: (this.currentPage - 1) * this.pageSize,
    SortDirection: SortDirection.Desc,
    SortColumn: ConsolGridOrderBy.DateEntered,
    ConsigneeName: '',
    ShipmentCode: '',
    OrganizationCode: ''
  };

  constructor(
    private trackerDataService: TrackerDataService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.authenticationService.logout(false);
  }

  public ngOnInit(): void {
    this.filter = Object.assign(ConsolListFilterRequest, this.defaultFilter);
    const isURLEmpty = this.getFilterValueFromURL();
    this.routerlinkPathFn = (consol) => {
      // return '/tracker/ShipmentCode=' + consol.ShipmentCode +
      // '&HouseBillNumber=' + consol.HouseBill + '&MasterBillNumber=' + consol.MasterBill;
      return '/tracker/' + consol.ShipmentCode;
    };
    if (isURLEmpty === false) {
      this.updateConsolList();
    }
  }

  public clearFilters(): void {
    this.currentPage = 1;
    this.totalCount = 0;
    this.consolList = null;
    this.filter = Object.assign(ConsolListFilterRequest, this.defaultFilter);
  }

  public updateFilters(): void {
    this.currentPage = 1;
    this.updateConsolList();
  }

  public pageChanged(): void {
    this.updateConsolList();
  }

  public updateConsolList(): void {
    this.filter.Skip = (this.currentPage - 1) * this.pageSize;
    this.filter.SearchFrom = 'Web';
    if (this.filter.MasterBillNumber == null) {
      this.filter.MasterBillNumber = '';
    }
    if (this.filter.HouseBillNumber == null) {
      this.filter.HouseBillNumber = '';
    }
    if (this.filter.ContainerNumber == null) {
      this.filter.ContainerNumber = '';
    }
    if (this.filter.CustomerRefNumber == null) {
      this.filter.CustomerRefNumber = '';
    }
    if (this.filter.ShipmentCode == null) {
      this.filter.ShipmentCode = '';
    }
    this.trackerDataService.getList(this.filter).pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(response => {
        this.totalCount = response.TotalRecords;
        this.consolList = response.Data;
        if (this.filter.MasterBillNumber !== null) {
          if (this.filter.MasterBillNumber.indexOf('-') !== -1 && this.consolList.length === 0) {
            this.NoResultMessage = 'Please search again by removing “-“ from ' + this.filter.MasterBillNumber;
          } else {
            this.NoResultMessage = 'Your search yielded no results. Please clear filters and try again.';
          }
        } else {
          if (this.consolList.length === 0) {
            this.NoResultMessage = 'Your search yielded no results. Please clear filters and try again.';
          }
        }
      });
    this.makeDynamicURL();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private makeDynamicURL(): void {
    this.router.navigate(['/consolidation'],
      {
        queryParams: {
          mb: this.filter.MasterBillNumber,
          hb: this.filter.HouseBillNumber,
          sc: this.filter.ContainerNumber,
          so: this.filter.CustomerRefNumber,
          sn: this.filter.ShipmentCode
        }
      });
    const params = { ...this.route.snapshot.queryParams };
    if (params.mb === undefined ||
      params.hb === undefined ||
      params.sc === undefined ||
      params.so === undefined ||
      params.sn === undefined) {
      params.mb = this.filter.MasterBillNumber;
      params.hb = this.filter.HouseBillNumber;
      params.sc = this.filter.ContainerNumber;
      params.so = this.filter.CustomerRefNumber;
      params.sn = this.filter.ShipmentCode;
    }
    if (this.filter.MasterBillNumber.length === 0) {
      delete params.mb;
    }
    if (this.filter.HouseBillNumber.length === 0) {
      delete params.hb;
    }
    if (this.filter.ContainerNumber == null) {
      delete params.sc;
    } else if (this.filter.ContainerNumber.length === 0) {
      delete params.sc;
    }
    if (this.filter.CustomerRefNumber === null) {
      delete params.so;
    } else if (this.filter.CustomerRefNumber.length === 0) {
      delete params.so;
    }
    if (this.filter.ShipmentCode == null) {
      delete params.sn;
    } else if (this.filter.ShipmentCode.length === 0) {
      delete params.sn;
    }
    this.router.navigate([], { queryParams: params });
  }

  private getFilterValueFromURL(): boolean {
    const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;
    const queryParmMasterBill = snapshot.root.children[0].queryParams['mb'];
    if (typeof queryParmMasterBill !== 'undefined') {
      this.filter.MasterBillNumber = queryParmMasterBill;
      sessionStorage.setItem('MBL', queryParmMasterBill);
    }
    const queryParmHouseBill = snapshot.root.children[0].queryParams['hb'];
    if (typeof queryParmHouseBill !== 'undefined') {
      this.filter.HouseBillNumber = queryParmHouseBill;
      sessionStorage.setItem('HBL', queryParmHouseBill);
    }
    const queryParmContainer = snapshot.root.children[0].queryParams['sc'];
    if (typeof queryParmContainer !== 'undefined') {
      this.filter.ContainerNumber = queryParmContainer;
      sessionStorage.setItem('Container', queryParmContainer);
    }
    const queryParmCustomerRef = snapshot.root.children[0].queryParams['so'];
    if (typeof queryParmCustomerRef !== 'undefined') {
      this.filter.CustomerRefNumber = queryParmCustomerRef;
      sessionStorage.setItem('CustomerRef', queryParmCustomerRef);
    }
    const queryParmShipmentCode = snapshot.root.children[0].queryParams['sn'];
    if (typeof queryParmShipmentCode !== 'undefined') {
      this.filter.ShipmentCode = queryParmShipmentCode;
      sessionStorage.setItem('ShipmentCode', queryParmShipmentCode);
    }

    if (typeof queryParmMasterBill === 'undefined' &&
      typeof queryParmHouseBill === 'undefined' &&
      typeof queryParmContainer === 'undefined' &&
      typeof queryParmCustomerRef === 'undefined' &&
      typeof queryParmShipmentCode === 'undefined') {
      return true;
    } else {
      return false;
    }
  }
}
